<div class="settings-section">

  <!-- ── Header row: title only ──────────────────────────────────────────── -->
  <div class="ps-header-row">
    <div class="ps-heading-group">
      <h3 class="section-heading">Participants</h3>
      <p class="section-desc">
        Email-based participants log in with their own credentials;
        ad-hoc participants use generated login IDs and passwords.
      </p>
    </div>
  </div>
  <!-- ── Capacity gutter ──────────────────────────────────────────────── -->
  <ng-container *ngIf="isCapacityLoading || maxParticipants !== null">

    <!-- Loading state -->
    <div *ngIf="isCapacityLoading" class="ps-gutter-wrap ps-gutter-loading">
      <mat-spinner diameter="14" class="ps-gutter-spinner"></mat-spinner>
      <span class="ps-gutter-loading-label">Calculating participant capacity from VM subscription quota…</span>
    </div>

    <!-- Quota loaded -->
    <ng-container *ngIf="!isCapacityLoading && maxParticipants !== null">
      <div class="ps-gutter-wrap" [class.ps-gutter-full]="isAtCapacity">
        <!-- Top row: counts + label + Info FAB -->
        <div class="ps-gutter-top">
          <div class="ps-gutter-counts">
            <span class="ps-gutter-used" [class.ps-gutter-used--full]="isAtCapacity">{{ participants.length }}</span>
            <span class="ps-gutter-sep">/</span>
            <span class="ps-gutter-max">{{ maxParticipants }}</span>
            <span class="ps-gutter-label-text">participant slot{{ maxParticipants !== 1 ? 's' : '' }}</span>
            <span class="ps-gutter-pct">({{ capacityBarPercent }}%)</span>
          </div>
          <div class="ps-gutter-right">
            <span class="ps-gutter-stale" *ngIf="isQuotaStale" matTooltip="Capacity is based on a cached VM quota snapshot that may be out of date. Refresh quota in VM Subscriptions for the latest figure.">
              <mat-icon class="ps-gutter-status-icon">history</mat-icon>
              Quota may be stale
            </span>
            <span class="ps-gutter-status" [class.ps-gutter-status--full]="isAtCapacity">
              <mat-icon class="ps-gutter-status-icon">{{ isAtCapacity ? 'block' : 'check_circle_outline' }}</mat-icon>
              {{ isAtCapacity ? 'Capacity reached' : (maxParticipants - participants.length) + ' remaining' }}
            </span>
            <button mat-flat-button
              color="primary"
              class="ps-info-fab"
              (click)="isCapacityInfoOpen = true">
              <mat-icon>info</mat-icon>
              Info
            </button>
          </div>
        </div>
        <!-- Bar -->
        <mat-progress-bar
          mode="determinate"
          [value]="capacityBarPercent"
          [style.--mdc-linear-progress-active-indicator-color]="capacityBarColor"
          [style.--mdc-linear-progress-track-color]="'#e0e0e0'"
          class="ps-gutter-bar">
        </mat-progress-bar>
      </div>
    </ng-container>
  </ng-container>

  <!-- ── Capacity info drawer ──────────────────────────────────────────── -->
  <!-- Backdrop: only rendered when open (clicking it closes the drawer) -->
  <div class="ps-drawer-backdrop" *ngIf="isCapacityInfoOpen" (click)="isCapacityInfoOpen = false"></div>
  <!-- Panel: always in the DOM so the CSS translate transition fires correctly -->
  <div class="ps-drawer" [class.ps-drawer--open]="isCapacityInfoOpen">

      <!-- Header -->
      <div class="ps-drawer-header">
        <div class="ps-drawer-header-left">
          <mat-icon class="ps-drawer-header-icon">speed</mat-icon>
          <span class="ps-drawer-title">Participant Capacity</span>
        </div>
        <button mat-icon-button (click)="isCapacityInfoOpen = false" matTooltip="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Hero summary -->
      <div class="ps-drawer-hero" [class.ps-drawer-hero--full]="isAtCapacity">
        <div class="ps-drawer-hero-top">
          <mat-icon class="ps-drawer-hero-icon">{{ isAtCapacity ? 'group_off' : 'groups' }}</mat-icon>
          <ng-container *ngIf="maxParticipants !== null; else drawerLoading">
            <span class="ps-drawer-hero-count">{{ participants.length }}<span class="ps-drawer-hero-max"> / {{ maxParticipants }}</span></span>
          </ng-container>
          <ng-template #drawerLoading>
            <mat-spinner diameter="22" style="margin-left:8px;"></mat-spinner>
          </ng-template>
        </div>
        <span class="ps-drawer-hero-label">{{ isAtCapacity ? 'All participant slots are filled' : 'participant slots used' }}</span>
      </div>

      <div class="ps-drawer-body">

        <!-- ── Constraint analysis ── -->
        <div class="ps-drawer-section">
          <div class="ps-drawer-section-hd">
            <mat-icon class="ps-drawer-section-hd-icon">analytics</mat-icon>
            Constraint Analysis
          </div>

          <!-- VM Subscriptions constraint card -->
          <div class="ps-drawer-card" *ngIf="vmWise !== null || isCapacityLoading"
            [class.ps-drawer-card--binding]="constraintSource === 'vm' || constraintSource === 'both'">
            <div class="ps-drawer-card-header">
              <div class="ps-drawer-card-title">
                <mat-icon class="ps-drawer-card-icon">dns</mat-icon>
                VM Subscriptions
              </div>
              <span class="ps-drawer-badge ps-drawer-badge--binding"
                *ngIf="constraintSource === 'vm'">LIMITING</span>
              <span class="ps-drawer-badge ps-drawer-badge--equal"
                *ngIf="constraintSource === 'both'">BOTH EQUAL</span>
            </div>
            <ng-container *ngIf="vmWise !== null; else vmLoadingCard">
              <div class="ps-drawer-calc">
                <div class="ps-drawer-calc-row">
                  <mat-icon class="ps-drawer-calc-icon">computer</mat-icon>
                  <span><strong>{{ vmWise + 1 }}</strong> class-VM slot{{ (vmWise + 1) !== 1 ? 's' : '' }} the VM subscriptions can support in total</span>
                </div>
                <div class="ps-drawer-calc-row ps-drawer-calc-row--deduct">
                  <mat-icon class="ps-drawer-calc-icon">remove_circle_outline</mat-icon>
                  <span>1 reserved for the class-template VM / AAD user</span>
                </div>
                <div class="ps-drawer-calc-result">
                  <mat-icon>arrow_forward</mat-icon>
                  <strong>{{ vmWise }}</strong> participant slot{{ vmWise !== 1 ? 's' : '' }}
                </div>
                <div class="ps-drawer-calc-row ps-drawer-calc-note" *ngIf="hasRemainingStat">
                  <mat-icon class="ps-drawer-calc-icon">bolt</mat-icon>
                  <span><strong>{{ remainingVmCapacity }}</strong> VM{{ remainingVmCapacity !== 1 ? 's' : '' }} provisionable right now (remaining core quota){{ isQuotaStale ? ' — quota may be out of date' : '' }}</span>
                </div>
              </div>
            </ng-container>
            <ng-template #vmLoadingCard>
              <div class="ps-drawer-loading-row">
                <mat-spinner diameter="14"></mat-spinner>
                <span>Loading VM quota data…</span>
              </div>
            </ng-template>
          </div>

          <!-- ACC Subscriptions constraint card -->
          <div class="ps-drawer-card" *ngIf="accWise !== null"
            [class.ps-drawer-card--binding]="constraintSource === 'acc' || constraintSource === 'both'">
            <div class="ps-drawer-card-header">
              <div class="ps-drawer-card-title">
                <mat-icon class="ps-drawer-card-icon">manage_accounts</mat-icon>
                Account Subscriptions
              </div>
              <span class="ps-drawer-badge ps-drawer-badge--binding"
                *ngIf="constraintSource === 'acc'">LIMITING</span>
              <span class="ps-drawer-badge ps-drawer-badge--equal"
                *ngIf="constraintSource === 'both'">BOTH EQUAL</span>
            </div>
            <div class="ps-drawer-calc">
              <div class="ps-drawer-calc-row">
                <mat-icon class="ps-drawer-calc-icon">person</mat-icon>
                <span><strong>{{ accWise + 1 }}</strong> account subscription{{ (accWise + 1) !== 1 ? 's' : '' }} added</span>
              </div>
              <div class="ps-drawer-calc-row ps-drawer-calc-row--deduct">
                <mat-icon class="ps-drawer-calc-icon">remove_circle_outline</mat-icon>
                <span>1 reserved for class-template AAD user</span>
              </div>
              <div class="ps-drawer-calc-result">
                <mat-icon>arrow_forward</mat-icon>
                <strong>{{ accWise }}</strong> participant slot{{ accWise !== 1 ? 's' : '' }}
              </div>
            </div>
          </div>

          <!-- No constraints -->
          <div *ngIf="vmWise === null && accWise === null && !isCapacityLoading" class="ps-drawer-no-constraint">
            <mat-icon>info_outline</mat-icon>
            <span>This class has no VM or Account Subscription resource types — participant count is unconstrained.</span>
          </div>
        </div>

        <!-- ── Verdict ── -->
        <ng-container *ngIf="maxParticipants !== null && constraintSource !== null">
          <div class="ps-drawer-section">
            <div class="ps-drawer-section-hd">
              <mat-icon class="ps-drawer-section-hd-icon">calculate</mat-icon>
              Effective Maximum
            </div>
            <div class="ps-drawer-verdict">
              <ng-container [ngSwitch]="constraintSource">
                <div *ngSwitchCase="'both'" class="ps-drawer-verdict-row">
                  <mat-icon class="ps-drawer-verdict-icon">balance</mat-icon>
                  <span>Both constraints are equal at <strong>{{ maxParticipants }}</strong> participant slots.</span>
                </div>
                <div *ngSwitchCase="'vm'" class="ps-drawer-verdict-row">
                  <mat-icon class="ps-drawer-verdict-icon">dns</mat-icon>
                  <span><strong>{{ maxParticipants }}</strong> — limited by <strong>VM Subscriptions</strong>.</span>
                </div>
                <div *ngSwitchCase="'acc'" class="ps-drawer-verdict-row">
                  <mat-icon class="ps-drawer-verdict-icon">manage_accounts</mat-icon>
                  <span><strong>{{ maxParticipants }}</strong> — limited by <strong>Account Subscriptions</strong>.</span>
                </div>
              </ng-container>
            </div>
          </div>
        </ng-container>

        <!-- ── How to increase ── -->
        <div class="ps-drawer-section">
          <div class="ps-drawer-section-hd">
            <mat-icon class="ps-drawer-section-hd-icon">trending_up</mat-icon>
            How to Increase Capacity
          </div>
          <div class="ps-drawer-tips">
            <div class="ps-drawer-tip" *ngIf="constraintSource === 'acc' || constraintSource === 'both'">
              <mat-icon class="ps-drawer-tip-icon">person_add</mat-icon>
              <span>Add more <strong>Account Subscriptions</strong> — each extra subscription adds one participant slot.</span>
            </div>
            <div class="ps-drawer-tip" *ngIf="constraintSource === 'vm' || constraintSource === 'both'">
              <mat-icon class="ps-drawer-tip-icon">add_circle_outline</mat-icon>
              <span>Add more <strong>VM Subscriptions</strong> with available core quota for the class VM SKU.</span>
            </div>
            <div class="ps-drawer-tip" *ngIf="constraintSource === null && !isCapacityLoading">
              <mat-icon class="ps-drawer-tip-icon">category</mat-icon>
              <span>Assign a VM or Account Subscription resource type to this class to apply a capacity constraint.</span>
            </div>
          </div>
        </div>

      </div><!-- /ps-drawer-body -->
    </div><!-- /ps-drawer -->
  <!-- ── Ad-hoc notice (inline, shown only when relevant) ──────────────── -->
  <div *ngIf="hasAdhocCredentials" class="ps-adhoc-notice">
    <mat-icon class="ps-adhoc-notice-icon">security</mat-icon>
    <span>
      {{ adhocCount }} ad-hoc participant{{ adhocCount !== 1 ? 's' : '' }}
      &mdash; export list to share credentials.
    </span>
  </div>

  <!-- ── Main control row: textarea (left) + 2-col button grid (right) ─── -->
  <div class="ps-control-row">
    <mat-form-field appearance="outline" class="ps-email-field">
      <mat-label>Add Email(s) — comma, semicolon, tab or newline separated</mat-label>
      <textarea matInput
        [formControl]="bulkAddCtrl"
        rows="5"
        placeholder="alice@example.com, bob@example.com&#10;carol@example.com&#10;&#10;Enter to add · Shift+Enter for new line"
        (keydown.Enter)="onBulkEnter($event)">
      </textarea>
    </mat-form-field>
    <div class="ps-button-grid">
      <!-- Row 1: add actions -->
      <button mat-flat-button color="primary"
        [disabled]="!bulkAddCtrl.value?.trim() || isSaving || isAtCapacity"
        (click)="addBulk()">
        <mat-icon>person_add</mat-icon> Add
      </button>
      <button mat-stroked-button color="accent"
        [matTooltip]="isAtCapacity ? 'Participant capacity reached' : 'Generate ad-hoc login credentials'"
        [disabled]="isAtCapacity"
        (click)="openAdhocDialog()">
        <mat-icon>group_add</mat-icon> Ad-Hoc
      </button>
      <!-- Row 2: remove actions -->
      <button mat-stroked-button color="warn"
        [disabled]="!participants.length || isSaving"
        matTooltip="Remove all participants"
        (click)="removeAll()">
        <mat-icon>group_remove</mat-icon> Remove All
      </button>
      <button mat-stroked-button color="warn"
        [disabled]="!hasAdhocCredentials || isSaving"
        matTooltip="Remove all ad-hoc participants"
        (click)="removeAdHoc()">
        <mat-icon>badge</mat-icon> Remove Ad-Hoc Only
      </button>
      <!-- Row 3: export buttons -->
      <div class="ps-export-row">
        <button mat-stroked-button color="primary"
          [disabled]="!participants.length"
          matTooltip="Export participant list as PDF — includes email participants and ad-hoc credentials"
          (click)="exportAll()">
          <mat-icon>picture_as_pdf</mat-icon>{{devTools ? ' ' : 'Export '}}PDF
        </button>
        <button mat-stroked-button color="primary"
          [disabled]="!participants.length"
          matTooltip="Export participant list as CSV/Excel — ad-hoc passwords included, email participants have empty password"
          (click)="exportCsv()">
          <mat-icon>table_chart</mat-icon>{{devTools ? ' ' : 'Export '}}CSV
        </button>
        <button mat-stroked-button *ngIf="devTools"
          [disabled]="!hasAdhocCredentials"
          matTooltip="Dev only — exports ad-hoc credentials as credentials.json for stress-test tooling"
          (click)="exportJson()">
          <mat-icon>data_object</mat-icon> JSON
        </button>
      </div>
    </div>
  </div>

  <!-- Loading ad-hoc data -->
  <div *ngIf="isLoadingAdhoc" class="ps-progress-row">
    <mat-progress-bar mode="indeterminate" color="accent"></mat-progress-bar>
    <small class="ps-progress-label">Loading participant details…</small>
  </div>

  <!-- Empty state -->
  <div *ngIf="!isLoadingAdhoc && participants.length === 0" class="empty-msg">
    <mat-icon>group_off</mat-icon>
    <p>No participants added yet.</p>
  </div>

  <!-- Table -->
  <div *ngIf="participants.length > 0" class="table-container">

    <table mat-table [dataSource]="tableSource" class="participants-table">

      <!-- Checkbox select column -->
      <ng-container matColumnDef="select">
        <th mat-header-cell *matHeaderCellDef>
          <mat-checkbox
            [checked]="isAllSelected()"
            [indeterminate]="selection.hasValue() && !isAllSelected()"
            (change)="masterToggle()">
          </mat-checkbox>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            [checked]="selection.isSelected(row)"
            (change)="selection.toggle(row)">
          </mat-checkbox>
        </td>
      </ng-container>

      <!-- Login ID column -->
      <ng-container matColumnDef="loginId">
        <th mat-header-cell *matHeaderCellDef>Login ID</th>
        <td mat-cell *matCellDef="let row">{{ row.loginId }}</td>
      </ng-container>

      <!-- Type badge column -->
      <ng-container matColumnDef="type">
        <th mat-header-cell *matHeaderCellDef>Type</th>
        <td mat-cell *matCellDef="let row">
          <span *ngIf="row.isAdhoc" class="type-chip type-chip-adhoc">
            <mat-icon class="chip-icon">badge</mat-icon> Ad-hoc
          </span>
          <span *ngIf="!row.isAdhoc" class="type-chip type-chip-email">
            <mat-icon class="chip-icon">alternate_email</mat-icon> Email
          </span>
        </td>
      </ng-container>

      <!-- Actions column — header shows "Remove Selected" when rows are checked.
           The th has a fixed width so its conditional button never shifts other columns. -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="ps-actions-header">
          <span class="ps-remove-selected-wrap">
            <button mat-stroked-button color="warn"
              *ngIf="selection.hasValue()"
              (click)="removeSelected()"
              class="ps-remove-selected-btn">
              <mat-icon>group_remove</mat-icon>
              Remove ({{ selection.selected.length }})
            </button>
          </span>
        </th>
        <td mat-cell *matCellDef="let row">
          <button mat-icon-button color="warn"
            matTooltip="Remove participant"
            (click)="removeParticipant(row.loginId)">
            <mat-icon>person_remove</mat-icon>
          </button>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
    </table>

    <mat-paginator [pageSizeOptions]="pageSizeOptions" [pageSize]="pageSize" appPersistPageSize="class:participants:list" showFirstLastButtons></mat-paginator>

    <p class="total-line">
      Total: {{ participants.length }} participant{{ participants.length !== 1 ? 's' : '' }}
      <span *ngIf="hasAdhocCredentials"> &mdash; {{ adhocCount }} ad-hoc</span>
    </p>

  </div><!-- /table-container -->

  <!-- Save progress -->
  <div *ngIf="isSaving" class="ps-progress-row">
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  </div>
</div><!-- /settings-section -->
